/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Edit page modal to be rendered on click of Add New store button inside store page of dedicated delivery.
 *
 * @version 1.0 13th May 2024
 * @author gbiswajeeta
 */

import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { MRT_Row } from "material-react-table";
import { useCallback, useEffect, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import { editClientInformation } from "../../../app/features/client/clientSlice";
import { getfdpList } from "../../../app/features/fdp/fdpSlice";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import { getparcelCenterList } from "../../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { UpdateClientRequest } from "../../../app/models/requestModels/clientRequest";
import { Client } from "../../../app/models/responseModels/clientsResponse";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import Loader from "../../../components/common/loader/Loader";
import {
  ADDRESS_REGEX,
  ADDRESS_REGEX_INVALID_MSG,
  CITY_REGEX,
  CITY_REGEX_INVALID_MSG,
  ZIPCODE_REGEX,
  ZIPCODE_REGEX_INVALID_MSG,
} from "../../../helpers/AppConstants";
import { STATES_LIST } from "../../fdp/FdpConstants";
import ClientContactGrid from "../contact/ClientContact";
import { CheckBox } from "@mui/icons-material";
import FdpNetPaymentTerms from "../fdp_payment_terms/FdpPaymentTerm";
import ClientThirdPartyVendors from "../third_party_vendor/ClientThirdPartyVendors";

type EditClientModalProps = {
  clientType: string;
  clientData: MRT_Row<Client>;
  onCompleted: Function;
  getAllClientData: Function;
};

const EditClientModal = (props: EditClientModalProps) => {
  const { authResponse } = useSelector((state: RootState) => state.login);

  const { isLoading, parcelCenterList } = useSelector(
    (state: RootState) => state.parcelCenter
  );

  const { isFdpLoading, fdpList } = useSelector(
    (state: RootState) => state.fdp
  );

  const [isFieldsReadOnly, setIsFieldsReadOnly] = useState<boolean>(true);

  const dispatch = useAppDispatch();

  const clientData = props.clientData.original;

  const rateCardData = clientData.rateCardConfig || {};

  useEffect(() => {
    getParcelCenterData();
  }, []);

  useEffect(() => {
    getAllFdps();
  }, []);

  const initialValues: UpdateClientRequest = {
    id: clientData.id,
    clientName: clientData.clientName,
    description: clientData.description,
    billingAddress: clientData.billingAddress,

    
    city: clientData.city,
    zipCode: clientData.zipCode,
    state: clientData.state,
    typeOfClient: props.clientType,
    dimWeightFactor:
      clientData.clientConfig.find((e) =>
        e.keyName === "dim_weight_factor" ? e.value : ""
      )?.value || "",
    invoiceStartDate: rateCardData.invoiceStartDate,
    frequency: rateCardData.frequency,


    isAutoGenerate: rateCardData.isAutoGenerate,
    netPaymentTerm: rateCardData.netPaymentTerm,
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
  };

  // validation rule while adding new store into the database
  // Validation schema for the form fields
  const validationSchema = Yup.object().shape({
    clientName: Yup.string().required("Client Name is required"),
    description: Yup.string().optional(),
    billingAddress: Yup.string()
      .required("Billing Address is required")
      .matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    city: Yup.string()
      .required("City is required")
      .matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    state: Yup.string().required("State is required"),
    zipCode: Yup.string()
      .required("Zip Code is required")
      .matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    dimWeightFactor: Yup.number().optional(),
    frequency: Yup.number().required(),
    waiveOffPackageCount: Yup.number()
    .min(0, "Package count must be at least 0")
    .max(99, "Package count cannot exceed 99")
    .integer("Package count must be a whole number")
    .typeError("Package count must be a number")
    .optional()

  });

  const handleSubmit = async (values: UpdateClientRequest) => {
    try {
      const response = await dispatch(editClientInformation(values)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "clientList",
        });
      }
      toast.success("Client information has been updated successfully.");
      props.getAllClientData();
      setIsFieldsReadOnly(true);
      
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientListException",
      });
    } finally {
      props.onCompleted();
    }
    return true;
  };

  const handleCancel = (resetForm: FormikHelpers<any>["resetForm"]) => {
    // Reset the form to its initial values
    //resetForm();
    props.getAllClientData();
    // Set the fields back to read-only mode
    setIsFieldsReadOnly(true);
  };

  const getParcelCenterData = useCallback(async () => {
    try {
      const response = await dispatch(getparcelCenterList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "parcelCenterList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterListException",
      });
    }
  }, [dispatch]);

  const getAllFdps = useCallback(async () => {
    try {
      const response = await dispatch(getfdpList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fdpData",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpDataException",
      });
    }
  }, [dispatch]);

  const invoiceFrequencyOptions = [
    { label: "Weekly", value: 7 },
    { label: "Bi-weekly", value: 14 },
    { label: "Monthly", value: 30 },
    { label: "45 days", value: 45 },
  ];

  return (
    <>
      <Loader isLoading={isLoading || isFdpLoading} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: any, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, values, setFieldValue, resetForm, handleChange }) => (
          <Form>
            {/* <Grid item xs={12}>
            <FormLabel component="legend">Client Type</FormLabel>
            <RadioGroup
              row
              name="clientType"
              value={values.typeOfClient}
              onChange={(event) =>
                setFieldValue("typeOfClient", event.target.value)
              }
            >
              <FormControlLabel
                value="dedicated"
                control={<Radio disabled={isFieldsReadOnly} />}
                label="Dedicated Delivery"
              />
              <FormControlLabel
                value="e2e"
                control={<Radio disabled={isFieldsReadOnly} />}
                label="Co-mingled Delivery"
              />
              <FormControlLabel
                value="both"
                control={<Radio disabled={isFieldsReadOnly} />}
                label="Both Way Of Delivery"
              />
            </RadioGroup>
          </Grid> */}
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid container item xs={12} justifyContent="space-between">
                <Grid item>
                  <Typography variant="h5" component="h3">
                    Client Details:
                  </Typography>
                </Grid>
                <Grid container justifyContent="right" columnGap={2}>
                  {isFieldsReadOnly && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        setIsFieldsReadOnly(!isFieldsReadOnly);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  {!isFieldsReadOnly && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSubmitting}
                        onClick={(e) => {
                          handleCancel(resetForm);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSubmitting}
                        type="submit"
                      >
                        Submit
                      </Button>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="id"
                  label="Client ID"
                  name="id"
                  size="small"
                  fullWidth
                  required
                  variant={"filled"}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="clientName"
                  label="Client Name"
                  name="clientName"
                  size="small"
                  fullWidth
                  required
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="description"
                  label="Description"
                  name="description"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="billingAddress"
                  label="Billing Address"
                  name="billingAddress"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="city"
                  label="City"
                  name="city"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  select
                  margin="normal"
                  id="state"
                  label="State"
                  name="state"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 150 } },
                    },
                  }}
                  required
                >
                  {STATES_LIST.map((state: string, index: number) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </FormikTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="zipCode"
                  label="Zip Code"
                  name="zipCode"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  required
                />
              </Grid>
              {(values.typeOfClient === "e2e" ||
                values.typeOfClient === "both") && (
                <Grid item xs={12} sm={6}>
                  <FormikTextField
                    margin="normal"
                    id="dimWeightFactor"
                    label="Dim Weight Factor"
                    name="dimWeightFactor"
                    size="small"
                    fullWidth
                    variant={!isFieldsReadOnly ? "outlined" : "filled"}
                    InputProps={{
                      readOnly: isFieldsReadOnly,
                    }}
                  />
                </Grid>
              )}
            </Grid>
            <Divider />
            <Grid container rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} marginTop={2}>
                <Typography variant="h5" component="h3">
                  Finance Details:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} margin={"auto"}>
                <FormControlLabel
                  control={
                    <Field
                      id="isAutoGenerate"
                      name="isAutoGenerate"
                      // component={CheckBox}
                      disabled={isFieldsReadOnly}
                      type="checkbox"
                      size="small"
                      onChange={handleChange}
                    />
                  }
                  label="Auto Generate"
                  name="isAutoGenerate"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {values.invoiceStartDate &&
                  new Date(values.invoiceStartDate) && (
                    <label
                      htmlFor="invoiceStartDate"
                      style={{
                        display: "block",
                        fontSize: "11px",
                        color: "#333",
                      }}
                    >
                      Invoice start date
                    </label>
                  )}
                <DatePicker
                  id="invoiceStartDate"
                  name="invoiceStartDate"
                  format="MM/DD/YYYY HH:mm:ss"
                  placeholder="Select Invoice Start Date"
                  style={{
                    width: "100%",
                    height: "36px",
                    marginTop: !values.invoiceStartDate ? "16px" : "0px",
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  plugins={[<TimePicker position="bottom" key={"hi"} />]}
                  onChange={(dateObject: DateObject) => {
                    setFieldValue(
                      "invoiceStartDate",
                      dateObject.format("YYYY-MM-DDTHH:mm:ss")
                    );
                  }}
                  value={
                    values.invoiceStartDate && new Date(values.invoiceStartDate)
                  }
                  disabled={isFieldsReadOnly}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  disableClearable
                  id="frequency"
                  options={invoiceFrequencyOptions.map(
                    (option) => option.label
                  )}
                  // options={invoiceFrequencyOptions}
                  // getOptionLabel={(option) =>
                  //   `${"label" in option ? option.label : `${option}`}`
                  // }
                  readOnly={isFieldsReadOnly}
                  value={values.frequency}
                  // value={invoiceFrequencyOptions.find(
                  //   (x) => values.frequency === x.value
                  // )}
                  onChange={(event, value) => {
                    console.log(`value = ${JSON.stringify(value)}`);
                    if (value === "Weekly") setFieldValue("frequency", 7);
                    else if (value === "Bi-weekly")
                      setFieldValue("frequency", 14);
                    else if (value === "Monthly")
                      setFieldValue("frequency", 30);
                    else if (value === "45 days")
                      setFieldValue("frequency", 45);
                    else setFieldValue("frequency", Number(value));

                    // setFieldValue("frequency", Number(value.value));
                    // if (value) {
                    //   if ("label" in value)
                    //     setFieldValue("frequency", value.value);
                    //   else setFieldValue("frequency", value);
                    // }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id="frequency"
                      name="frequency"
                      label="Invoice Frequency"
                      margin="normal"
                      size="small"
                      variant={!isFieldsReadOnly ? "outlined" : "filled"}
                      aria-readonly={isFieldsReadOnly}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="frequency"
                  label="Invoice Frequency"
                  name="frequency"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 150 } },
                    },
                  }}
                />
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <FormikTextField
                  margin="normal"
                  id="netPaymentTerm"
                  label="Net Payment Term"
                  name="netPaymentTerm"
                  size="small"
                  fullWidth
                  variant={!isFieldsReadOnly ? "outlined" : "filled"}
                  InputProps={{
                    readOnly: isFieldsReadOnly,
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: { style: { maxHeight: 150 } },
                    },
                  }}
                />
              </Grid>
             
            </Grid>
            {/* <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12} marginTop={2}>
              <Typography variant="h5" component="h3">
                Contact Details:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormikTextField
                margin="normal"
                id="contactName"
                label="Contact Name"
                name="contactPersonName"
                size="small"
                fullWidth
                variant={!isFieldsReadOnly ? "outlined" : "filled"}
                InputProps={{
                  readOnly: isFieldsReadOnly,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormikTextField
                margin="normal"
                id="jobTitle"
                label="Job Title"
                name="contactPersonJobTitle"
                size="small"
                fullWidth
                variant={!isFieldsReadOnly ? "outlined" : "filled"}
                InputProps={{
                  readOnly: isFieldsReadOnly,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormikTextField
                margin="normal"
                id="email"
                label="Email"
                name="contactPersonEmail"
                size="small"
                fullWidth
                variant={!isFieldsReadOnly ? "outlined" : "filled"}
                InputProps={{
                  readOnly: isFieldsReadOnly,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormikTextField
                margin="normal"
                id="phoneNumber"
                label="Phone Number"
                name="contactPersonPhoneNumber"
                size="small"
                fullWidth
                variant={!isFieldsReadOnly ? "outlined" : "filled"}
                InputProps={{
                  readOnly: isFieldsReadOnly,
                }}
              />
            </Grid>
          </Grid> */}
          </Form>
        )}
      </Formik>

<FdpNetPaymentTerms
  clientData={clientData}
/>

<ClientThirdPartyVendors clientData={clientData}></ClientThirdPartyVendors>

<Card>
        <CardHeader subheader="Contact Details" />
        <CardContent>
          <ClientContactGrid data={clientData.clientContacts || []} />
        </CardContent>
      </Card>
      {/* <Card>
        <CardHeader subheader="Associated Parcel Centers" />
        <CardContent>
          <AssociatedParcelCenterGrid
            data={clientData.clientParcelCenters || []}
            parcelCenterData={parcelCenterList.filter((e) =>
              clientData.clientParcelCenters
                .map((e) => e.parcelCenterId)
                .includes(parseInt(e.parcelCenterId))
            )}
          />
        </CardContent>
      </Card> */}
    </>
  );
};
export default EditClientModal;
